/** The activity's "Where it's used" panel (plan §3, the record's signature element): the
 *  first panel under the band, full width, read-only — a sibling of the record's cards: the
 *  same `SectionCard` and heading as `RecordPanel`, and a region named by its title.
 *
 *  - Header: "Where it's used" · Open in Usage › (the Usage tab: the full, paged list).
 *  - One row per relation that holds the activity — Tours · Catalog itineraries · Orphan
 *    day plans · Unlisted trips · Line-set stops · Nearby of (the used-by's groups) — its
 *    label and count, then its first five rows: each a link to the record (an orphan day plan
 *    has no page: plain text) with the facts that make an edit or a delete risky (the day the
 *    activity is on, a stop's distance, "blocks Delete" — every relation but Nearby of: the
 *    delete rule's, `whereUsedModel`), then "N more in Usage ›". Unlisted
 *    trips are counted, never listed ("1,000+" past the backend's cap): one line says what
 *    they are, and that they block Delete too.
 *  - Empty: "Not used anywhere — safe to delete". Failed: what happened, and Retry.
 *  - Loading: the loaded panel's shape from the detail's cheap counts (`usage`) — per
 *    relation they count, its label and min(count, 5) rows (and the "more" line past five),
 *    on the loaded rows' own boxes; the empty line when they count nothing — so the rows
 *    land where the skeleton stood. The tours and trips are not among those counts.
 *
 *  Its input is used-by pages in the API's shape — the first page, or one page per type;
 *  the counts come from their `available_types`. Given the `activity`, a group's label opens
 *  its full list in place — the records' relation drawer, paged (`WhereUsedGroupDrawer`);
 *  without it — and always for the unlisted trips — the label is plain text. */
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Button, Link, Skeleton, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { ActivityRow, ActivityUsage, ActivityUsedByList } from '@/api/activities';
import { SectionCard } from '@/components/ReadView';
import { RecordCardSkeleton } from '@/features/records/page/RecordStates';

import { activityTitle } from './activityNames';
import { WhereUsedGroupDrawer } from './WhereUsedGroupDrawer';
import { WHERE_USED_ROW_SX, WhereUsedRow } from './WhereUsedRow';
import {
  groupCountText,
  isUnused,
  whereUsedGroups,
  whereUsedSkeleton,
  WHERE_USED_EMPTY,
  WHERE_USED_FIRST,
  WHERE_USED_LABEL,
  WHERE_USED_UNLISTED,
  type WhereUsedGroup,
  type WhereUsedGroupKey,
  type WhereUsedSkeletonGroup,
} from './whereUsedModel';

export const WHERE_USED_ANCHOR_ID = 'activity-where-used';

const TITLE = 'Where it’s used';
const LABEL_COLUMN_PX = 168;

/** One relation: its label beside (above, on a phone) its rows — loaded or skeleton alike. */
const GROUP_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: `${LABEL_COLUMN_PX}px minmax(0, 1fr)` },
  columnGap: 2,
  rowGap: 0.25,
  py: 1,
  px: 1,
  borderTop: 1,
  borderColor: 'divider',
  '&:first-of-type': { borderTop: 0, pt: 0 },
  fontVariantNumeric: 'tabular-nums',
} as const;
const ROWS_SX = { listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.25 } as const;
const EMPTY_SX = { px: 1 } as const;
/** The skeleton rows' widths, so they read as names of different lengths. */
const SKELETON_ROW_WIDTHS = ['72%', '56%', '64%', '48%', '60%'];

export interface WhereUsedPanelProps {
  /** The used-by (`GET /activities/{id}/used_by/`): its first page, or one page per type;
   *  undefined while it loads. */
  usedBy: ActivityUsedByList | readonly ActivityUsedByList[] | undefined;
  /** The Usage tab. */
  usageHref: string;
  /** The rows could not be loaded. */
  failed?: boolean;
  onRetry?: () => void;
  /** The activity: its relations' labels open their full lists in the relation drawer. */
  activity?: Pick<ActivityRow, 'id' | 'name' | 'display_name'>;
  /** The detail's cheap counts: the loading skeleton takes the loaded panel's shape from
   *  them; without them it is one line. */
  usage?: Pick<ActivityUsage, 'catalog_itineraries' | 'stops' | 'nearby_of'>;
}

export function WhereUsedPanel({
  usedBy,
  usageHref,
  failed = false,
  onRetry,
  activity,
  usage,
}: WhereUsedPanelProps) {
  const groups = useMemo(() => (usedBy ? whereUsedGroups(usedBy) : undefined), [usedBy]);
  // The relation in the drawer stays set while it slides out.
  const [drawer, setDrawer] = useState<{ group: WhereUsedGroupKey; open: boolean } | null>(null);
  const onOpen = activity
    ? (group: WhereUsedGroupKey) => setDrawer({ group, open: true })
    : undefined;

  let body;
  if (failed && !groups) {
    body = (
      <Box
        role="alert"
        sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1, color: 'error.main' }}
        data-where-used-error
      >
        <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16 }} />
        <Typography variant="body2" component="span" color="text.primary">
          Couldn’t load where this activity is used.
        </Typography>
        {onRetry && (
          <Button size="small" onClick={onRetry} sx={{ minWidth: 0, py: 0 }}>
            Retry
          </Button>
        )}
      </Box>
    );
  } else if (!groups) {
    const shape = usage ? whereUsedSkeleton(usage) : [];
    body = (
      <Box role="status" aria-label="Loading where it’s used" data-where-used-loading>
        {shape.length ? (
          shape.map((g) => <GroupSkeleton key={g.key} group={g} />)
        ) : (
          <Typography variant="body2" sx={EMPTY_SX} data-where-used-skeleton-empty>
            <Skeleton width="40%" />
          </Typography>
        )}
      </Box>
    );
  } else if (isUnused(groups)) {
    body = (
      <Typography variant="body2" color="text.secondary" sx={EMPTY_SX} data-where-used-empty>
        {WHERE_USED_EMPTY}
      </Typography>
    );
  } else {
    body = (
      <Box component="dl" sx={{ m: 0 }}>
        {groups.map((g) =>
          g.rows.length || g.count ? (
            <Group key={g.key} group={g} usageHref={usageHref} onOpen={onOpen} />
          ) : null,
        )}
      </Box>
    );
  }

  // The header keeps one height in every state: the title and Open in Usage are always there.
  return (
    <Box
      component="section"
      id={WHERE_USED_ANCHOR_ID}
      tabIndex={-1}
      aria-label={TITLE}
      data-where-used
      sx={{
        outline: 'none',
        borderRadius: 1,
        '&:focus-visible': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` },
        // The cards' column spaces the panels (RecordPanel's rule).
        '& > .MuiCard-root': { mb: 0 },
      }}
    >
      <SectionCard
        title={TITLE}
        action={
          <Link component={RouterLink} to={usageHref} underline="hover" sx={{ typography: 'body2', fontWeight: 600 }}>
            Open in Usage ›
          </Link>
        }
      >
        {body}
      </SectionCard>
      {activity && drawer && (
        <WhereUsedGroupDrawer
          activityId={activity.id}
          activityName={activityTitle(activity)}
          group={drawer.group}
          open={drawer.open}
          onClose={() => setDrawer({ ...drawer, open: false })}
        />
      )}
    </Box>
  );
}

function Group({
  group,
  usageHref,
  onOpen,
}: {
  group: WhereUsedGroup;
  usageHref: string;
  onOpen: ((group: WhereUsedGroupKey) => void) | undefined;
}) {
  const label = WHERE_USED_LABEL[group.key];
  const countText = ` · ${groupCountText(group)}`;
  const shown = group.rows.slice(0, WHERE_USED_FIRST);
  const more = group.countOnly ? 0 : group.count - shown.length;
  return (
    <Box data-where-used-group={group.key} sx={GROUP_SX}>
      <Typography component="dt" variant="body2" sx={{ fontWeight: 600, minWidth: 0 }}>
        {onOpen && !group.countOnly ? (
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="hover"
            onClick={() => onOpen(group.key)}
            sx={{ fontWeight: 600, verticalAlign: 'baseline', textAlign: 'left' }}
          >
            {label}
            {countText}
          </Link>
        ) : (
          <>
            {label}
            <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
              {countText}
            </Box>
          </>
        )}
      </Typography>
      <Box component="dd" sx={{ m: 0, minWidth: 0 }}>
        {group.countOnly ? (
          <Typography variant="body2" color="text.secondary" data-where-used-unlisted>
            {WHERE_USED_UNLISTED}
          </Typography>
        ) : (
          <Box component="ul" sx={ROWS_SX}>
            {shown.map((row) => (
              <WhereUsedRow key={row.id} group={group.key} row={row} />
            ))}
          </Box>
        )}
        {more > 0 && (
          <Link
            component={RouterLink}
            to={usageHref}
            underline="hover"
            variant="caption"
            sx={{ fontWeight: 600 }}
            data-where-used-more
          >
            {`${more.toLocaleString('en-US')} more in Usage ›`}
          </Link>
        )}
      </Box>
    </Box>
  );
}

/** A relation while the rows load, on the loaded group's boxes: its label line, its rows
 *  (one `body2` line each, the rows' gap) and the "more" line past five. Not a list: the
 *  status says what loads. */
function GroupSkeleton({ group }: { group: WhereUsedSkeletonGroup }) {
  return (
    <Box data-where-used-skeleton-group={group.key} sx={GROUP_SX}>
      <Typography variant="body2" component="div" sx={{ fontWeight: 600, minWidth: 0 }}>
        <Skeleton width="60%" />
      </Typography>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={ROWS_SX}>
          {Array.from({ length: group.rows }, (_, i) => (
            <Box key={i} sx={WHERE_USED_ROW_SX} data-where-used-skeleton-row>
              <Skeleton width={SKELETON_ROW_WIDTHS[i % SKELETON_ROW_WIDTHS.length]} />
            </Box>
          ))}
        </Box>
        {group.more && (
          <Typography variant="caption" component="span" data-where-used-skeleton-more>
            <Skeleton width={120} sx={{ display: 'inline-block' }} />
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/** The panel while the record page loads (no detail yet): its card, the title row with Open
 *  in Usage's room, and the one line it loads in when nothing is counted yet. */
export function WhereUsedPanelSkeleton() {
  return (
    <RecordCardSkeleton
      action={
        <Typography variant="body2" component="div">
          <Skeleton width={100} />
        </Typography>
      }
    >
      <Typography variant="body2" component="div" sx={EMPTY_SX} data-where-used-panel-skeleton>
        <Skeleton width="40%" />
      </Typography>
    </RecordCardSkeleton>
  );
}
